/**
 * A tiny, dependency-free renderer for the card details briefing (spec #316:
 * "the Trello card description rendered as safe markdown, links open in a
 * new tab"). Headings, lists, GFM tables, blockquotes, `**bold**`, markdown links and bare URLs - safe by
 * construction, because it builds React elements and never touches
 * `dangerouslySetInnerHTML`, not a full CommonMark implementation.
 *
 * @module web/screens/safeMarkdown
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { parseMarkdownBlocks, planBriefingSections, splitVersionNote, type Block, type BriefingSection } from "../../../briefing-blocks.ts";

export { parseMarkdownBlocks };

/**
 * Inline tokens, in priority order: a markdown link `[label](https://url "title")`
 * (what Trello stores when a URL is pasted into a description - label and
 * target are usually the same URL), an `<https://url>` autolink, `**bold**`,
 * and a bare URL. Before 2026-11-02 only the last two existed, so a Trello
 * link rendered as a broken `https://x](https://x` anchor plus stray text.
 */
const INLINE_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)(?:\s+"[^"]*")?\)|<(https?:\/\/[^\s<>]+)>|\*\*([^*]+)\*\*|\bhttps?:\/\/[^\s<>()"'\]\[]+/g;

function link(url: string, label: string, key: string): ReactNode {
  return (
    <a key={key} href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

/** Markdown links, autolinks, `**bold**` and bare URLs within one block of text - all safe, never raw HTML. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const index = m.index ?? 0;
    if (index > lastIndex) out.push(text.slice(lastIndex, index));
    const k = `${keyPrefix}-${key++}`;
    if (m[1] !== undefined && m[2] !== undefined) {
      out.push(link(m[2], m[1], k));
      lastIndex = index + m[0].length;
    } else if (m[3] !== undefined) {
      out.push(link(m[3], m[3], k));
      lastIndex = index + m[0].length;
    } else if (m[4] !== undefined) {
      out.push(<strong key={k}>{renderInline(m[4], k)}</strong>);
      lastIndex = index + m[0].length;
    } else {
      const url = m[0].replace(/[.,;:!?]+$/, "");
      out.push(link(url, url, k));
      lastIndex = index + url.length;
    }
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

/**
 * Short plain text (a brand's offer note) with its links made clickable and
 * its line breaks kept - the inline half of `SafeMarkdown`, no block parsing.
 */
export function LinkifiedText({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  return (
    <>
      {lines.map((line, i) => (
        <span key={`l${i}`}>
          {i > 0 && <br />}
          {renderInline(line, `l${i}`)}
        </span>
      ))}
    </>
  );
}

/** A table in its own scroll wrapper, so a wide script scrolls here and never the page. A row with a cell too many widens the table: no word is ever dropped. */
function SafeTable({ block, keyPrefix }: { block: Extract<Block, { kind: "table" }>; keyPrefix: string }) {
  const columns = Math.max(block.header.length, ...block.rows.map((row) => row.length));
  const cells = Array.from({ length: columns }, (_, c) => c);
  const style = (c: number) => {
    const align = block.align[c];
    return align ? { textAlign: align } : undefined;
  };
  return (
    <div className="md-table-wrap" tabIndex={0}>
      <table>
        <thead>
          <tr>
            {cells.map((c) => (
              <th key={c} style={style(c)}>
                {renderInline(block.header[c] ?? "", `${keyPrefix}-h${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r}>
              {cells.map((c) => (
                <td key={c} style={style(c)}>
                  {renderInline(row[c] ?? "", `${keyPrefix}-${r}-${c}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Renders briefing text as safe markdown (headings, lists, tables, blockquotes, bold, auto-linked URLs opening in a new tab). */
export function SafeMarkdown({ text }: { text: string }) {
  return (
    <>
      {parseMarkdownBlocks(text).map((block, i) => {
        const key = `b${i}`;
        if (block.kind === "heading") return <h4 key={key}>{renderInline(block.text, key)}</h4>;
        if (block.kind === "quote") return <blockquote key={key}>{renderInline(block.text, key)}</blockquote>;
        if (block.kind === "table") return <SafeTable key={key} block={block} keyPrefix={key} />;
        if (block.kind === "list") {
          const items = block.items.map((item, j) => <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>);
          return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
        }
        return <p key={key}>{renderInline(block.text, key)}</p>;
      })}
    </>
  );
}

interface SectionLabels {
  openAllLabel: string;
  closeAllLabel: string;
}

/**
 * A long briefing, one native `<details>` per section (`planBriefingSections`). Closed
 * text stays in the page, which is what lets the browser's find in page reach it and
 * open its section. A selection copy would still skip a closed section, so the copy
 * event opens the ones the selection touches first.
 */
function BriefingSections({ sections, openAllLabel, closeAllLabel }: { sections: BriefingSection[] } & SectionLabels) {
  const [open, setOpen] = useState(() => sections.map((s) => s.open));
  const allOpen = open.every(Boolean);
  const wrap = useRef<HTMLDivElement>(null);

  // On the document: a selection that starts above the sections, a select-all above all, fires its copy there.
  useEffect(() => {
    function openSelected() {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed || !wrap.current) return;
      const range = selection.getRangeAt(0);
      const details = Array.from(wrap.current.querySelectorAll<HTMLDetailsElement>("details.cd-section"));
      const touched = details.map((d, i) => (range.intersectsNode(d) ? i : -1)).filter((i) => i >= 0 && !details[i]!.open);
      for (const i of touched) details[i]!.open = true;
      if (touched.length) setOpen((prev) => prev.map((value, i) => value || touched.includes(i)));
    }
    document.addEventListener("copy", openSelected);
    return () => document.removeEventListener("copy", openSelected);
  }, []);

  return (
    <div data-briefing-sections ref={wrap}>
      <div className="cd-section-tools">
        <button type="button" className="cd-section-toggle" data-briefing-toggle onClick={() => setOpen(sections.map(() => !allOpen))}>
          {allOpen ? closeAllLabel : openAllLabel}
        </button>
      </div>
      {sections.map((section, i) => (
        <details
          key={i}
          className="cd-section"
          open={open[i]}
          onToggle={(e) => {
            const value = e.currentTarget.open;
            setOpen((prev) => (prev[i] === value ? prev : prev.map((v, at) => (at === i ? value : v))));
          }}
        >
          <summary>{renderInline(section.heading, `s${i}`)}</summary>
          <div className="cd-section-body">
            <SafeMarkdown text={section.body} />
          </div>
        </details>
      ))}
    </div>
  );
}

/**
 * The card page's briefing: the writer's version note folded away, a long briefing's
 * sections folded (`briefing-blocks.ts#planBriefingSections`), the rest as `SafeMarkdown`.
 * Decided as it is drawn; the stored text is untouched.
 */
export function BriefingText({ text, noteLabel, openAllLabel, closeAllLabel }: { text: string; noteLabel: string } & SectionLabels) {
  const { note, body } = useMemo(() => splitVersionNote(text), [text]);
  const plan = useMemo(() => planBriefingSections(body), [body]);
  return (
    <>
      {note.length > 0 && (
        <details className="cd-version-note">
          <summary>{noteLabel}</summary>
          {note.map((line, i) => (
            <p key={i}>{renderInline(line, `n${i}`)}</p>
          ))}
        </details>
      )}
      {plan ? (
        <>
          <SafeMarkdown text={plan.intro} />
          <BriefingSections key={body} sections={plan.sections} openAllLabel={openAllLabel} closeAllLabel={closeAllLabel} />
        </>
      ) : (
        <SafeMarkdown text={body} />
      )}
    </>
  );
}
